import { Padel24Logo } from '../ui';
import { useT } from '../../i18n';

export interface NoScopeScreenProps {
  email: string | null;
  onSignOut: () => void;
  /**
   * WHY there is no scope.
   *
   *   'unavailable'  the server answered: this account holds no membership.
   *   'undetermined' the question failed: we do not know.
   *
   * Two different facts with two different remedies — one needs an
   * administrator, the other needs a retry.
   */
  reason?: 'unavailable' | 'undetermined';
  /** Ask again. Only meaningful when the discovery failed. */
  onRetry?: () => void;
}

/**
 * Signed in, and no organization to work in (#186).
 *
 * Two distinct reasons, deliberately NOT collapsed into one message:
 *
 *   'unavailable'   a real state, not an error. Authentication identifies a
 *                   person, and being identified is not the same as being
 *                   entitled to anything. It happens to a new account before
 *                   anyone grants it membership, and to an existing one the
 *                   moment that membership is removed. Only somebody else can
 *                   change it, so there is nothing to retry.
 *
 *   'undetermined'  we could not find out. Telling this person they have no
 *                   access would send them to ask for permission they may
 *                   already hold, and would hide an outage behind a message
 *                   that sounds like policy. So it says so, and offers a retry.
 *
 * Neither bounces back to the login screen. Someone typing correct credentials
 * and landing on a login screen concludes the password is wrong and tries it
 * again — which is true of neither case.
 */
export function NoScopeScreen({
  email,
  onSignOut,
  reason = 'unavailable',
  onRetry,
}: NoScopeScreenProps) {
  const t = useT();
  const failed = reason === 'undetermined';
  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: '#6b21a8' }}
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Padel24Logo size="xl" variant="full" tone="light" />
          </div>
          {/* ON THE PURPLE, NOT ON A CARD. The ink scale is chosen for
              `--paper`; `text-ink-3` on this `#6b21a8` is 2.78:1, under the
              4.5:1 floor (#781). 80% white is 6.11:1. Found across six screens
              at once when the contrast guard stopped being a hand-written list
              of two (#752). */}
          <p className="text-sm text-white/80">{t('app.tagline')}</p>
        </div>

        <div className="card p-6 text-center">
          <h2 className="font-display text-lg font-semibold mb-2">
            {failed ? t('auth.noScope.notFound') : t('auth.noScope.none')}
          </h2>
          <p className="text-sm text-ink-2 leading-relaxed">
            {t(failed ? 'auth.noScope.lookupFailed' : 'auth.noScope.noOrg', {
              // Nested interpolation, kept as data: the optional «som x» is
              // its own catalog entry, so a translator can move it or drop it
              // without the sentence being rebuilt in code.
              as: email ? t('auth.noScope.as', { email }) : '',
            })}
          </p>
          {failed && onRetry && (
            <button type="button" onClick={onRetry} className="btn-primary w-full mt-4 text-sm">
              {t('app.retry')}
            </button>
          )}
          <button type="button" onClick={onSignOut} className="btn-ghost w-full mt-3 text-xs">
            {t('app.signOut')}
          </button>
        </div>

        <p className="text-center text-[11px] text-white/70 mt-6">
          {t('app.footer', { brand: 'Starpoint' })}
        </p>
      </div>
    </div>
  );
}
